// Copyright (c) 2018 Cigent Technology Inc.

import { Component, DestroyRef, ElementRef, ViewChild } from '@angular/core';
import { HeaderService } from '../../header.service';
import { TranslateService } from '@ngx-translate/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Params, Router } from '@angular/router';
import {
    IAddFdeSerialData,
    IFdeLicense,
    IFdeLicenseResponse,
    IFdeQuery,
    IFdeQuerySort,
    SerialLicensingOption,
    SortInput
} from '../../../types/licensedDrives';
import { IColumnChooserElement } from '../../shared/components/column-chooser/column-chooser.component';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { D3eService } from '../../../services/d3e-service';
import { SelectedOrgMethod } from '../../../shared/selectedOrg';
import { NotificationService } from '../../../services/notification-service';
import { BehaviorSubject, combineLatest, EMPTY, firstValueFrom, forkJoin, Observable, of } from 'rxjs';
import { ALL_ORGS_ID } from '../../../types/group';
import { pairwise, startWith, switchMap, take, tap, withLatestFrom } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { EventsService } from '../../../services/events-service';
import { ClipboardService } from '../../../services/clipboard.service';
import { EVENT_TYPES } from '../../../types/events';
import { FdeLicenseDetailsComponent } from './fde-license-details/fde-license-details.component';
import { AddWorkstationDialogComponent } from './add-workstation-dialog/add-workstation-dialog.component';
import { isCustomer, isMSP } from '../../../shared/permissions-helper';
import { IManageUser } from '../../../types/user';
import { selectUser } from '../../store/user/user.selectors';
import { Store } from '@ngrx/store';
import { AppState } from '../../store/app.state';
import { EnterFdeRequestDialogComponent } from './enter-fde-request-dialog/enter-fde-request-dialog.component';
import { HttpStatusCode } from '@angular/common/http';
import { withLoading } from '../../../shared/misc';
import { MatSort, Sort, SortDirection } from '@angular/material/sort';
import { SelectionModel } from '@angular/cdk/collections';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { GenerateFdeLicenseDialogComponent } from './generate-fde-license-dialog/generate-fde-license-dialog.component';
import { AlertDialogComponent } from '../../dialogs/alert/alert.component';
import { MatButtonToggleChange } from '@angular/material/button-toggle';

dayjs.extend(utc);

const toSorts = (sort: SortInput): IFdeQuerySort[] => {
  const direction = sort?.direction === 'desc';
  const sortableAttributes = ["fde_license_id", "fde_serial_uuid", "created_dttm", "last_generated", "maint_exp_timestamp"];
  return sortableAttributes.includes(sort?.active) ? [{ attr: sort.active, desc: direction }] : [];
};

const toFdeQuery = (
  orgId: string,
  pageStart: number,
  pageSize: number,
  subscriptionId: string,
  assigned: boolean | null,
  sort: SortInput
): IFdeQuery => ({
  selectedOrgId: orgId === ALL_ORGS_ID ? undefined : orgId,
  pageStart,
  pageSize,
  subscriptionId,
  assigned,
  sorts: toSorts(sort),
});

@Component({
    selector: 'app-assign-fde-licenses',
    templateUrl: './assign-fde-licenses.component.html',
    styleUrl: './assign-fde-licenses.component.scss',
    standalone: false
})
export class AssignFdeLicensesComponent {
    @ViewChild('tableContainer') tableContainer!: ElementRef;
    @ViewChild(MatSort) matSort!: MatSort;
    private assigned: boolean | null = null;
    private pageStart = 0;
    private pageSize = 50;
    private lastClickedIndex: number | null = null;
    protected readonly isMSP = isMSP;
    protected readonly isCustomer = isCustomer;
    form: UntypedFormGroup;
    query$: BehaviorSubject<IFdeQuery> = new BehaviorSubject<IFdeQuery>(toFdeQuery('All', 0, this.pageSize, "", null, {}));
    licenses$: BehaviorSubject<IFdeLicense[]> = new BehaviorSubject<IFdeLicense[]>([]);
    subscriptionCount$: BehaviorSubject<number> = new BehaviorSubject<number>(0);
    totalSerials$: BehaviorSubject<number> = new BehaviorSubject<number>(0);
    totalFiltered$: BehaviorSubject<number> = new BehaviorSubject(0);
    totalLicensed$: BehaviorSubject<number> = new BehaviorSubject(0);
    sort$: BehaviorSubject<any> = new BehaviorSubject({active: 'created_dttm', direction: 'desc'});
    sortAttr = 'created_dttm';
    sortDir: SortDirection = 'desc';
    user$: Observable<IManageUser> = this.store.select(selectUser());
    isLoading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
    loadingSkeletonRows: {}[];
    forceCallback = true;
    MAX_DELETE_COUNT = 250;

    columns: IColumnChooserElement[] = [
        {
            title: this.translate.instant('assign.fde.columns.workstation-id'),
            value: 'fde_serial_uuid',
            selected: true,
            hidden: false,
            minWidth: '244px',
        },
        {
            title: this.translate.instant('assign.fde.columns.created'),
            value: 'created_dttm',
            selected: true,
            hidden: false,
            minWidth: '257px',
        },
        {
            title: this.translate.instant('assign.fde.columns.last-generated'),
            value: 'last_generated',
            selected: true,
            hidden: false,
            minWidth: '257px',
        },
        {
            title: this.translate.instant('assign.fde.columns.license-id'),
            value: 'fde_license_id',
            selected: true,
            hidden: false,
            minWidth: '145px',
        },
        {
            title: this.translate.instant('assign.columns.maintenance-date'),
            value: 'maint_exp_timestamp',
            selected: true,
            hidden: false,
            minWidth: '213px',
        },
        {
            title: this.translate.instant('assign.columns.license-key'),
            value: 'license_key',
            selected: true,
            hidden: true,
            minWidth: '162px',
        },
    ];

    defaultFdeMaintenanceDate: Date = dayjs.utc().add(1, 'year').endOf('day').toDate();
    maintenanceExpiration: Date;
    columnsToDisplay = this.columns.map(e => e.value);
    mspColumnsToDisplay = ['select', ...this.columnsToDisplay];
    licenseSelection: SelectionModel<IFdeLicense> = new SelectionModel<IFdeLicense>(true, []);

    constructor(
        private readonly headerService: HeaderService,
        private readonly translate: TranslateService,
        private readonly dialog: MatDialog,
        private readonly router: Router,
        private readonly activatedRoute: ActivatedRoute,
        private readonly formBuilder: UntypedFormBuilder,
        private readonly d3eService: D3eService,
        private readonly notification: NotificationService,
        private readonly destroyRef: DestroyRef,
        private readonly eventsService: EventsService,
        private readonly clipboard: ClipboardService,
        private readonly store: Store<AppState>,
    ) {
        headerService.setTitle(translate.instant('assign-fde.header'));
        headerService.setSubtitle('');
        headerService.setIcon('mdi-file-certificate-outline');
        headerService.setNavMenuText('');
        headerService.showUserFilter(true);
        this.form = this.formBuilder.group({
            code: ['']
        });

        const navigation = this.router.currentNavigation();
        const assignedLicenses = navigation?.extras?.state?.assignedLicenses;
        const validAssignedLicenses = assignedLicenses && assignedLicenses > 0 ? assignedLicenses : this.pageSize;
        const skeletonRowCount = validAssignedLicenses > this.pageSize ? this.pageSize : validAssignedLicenses;
        this.loadingSkeletonRows = Array(skeletonRowCount).fill({});
        this.maintenanceExpiration = new Date(navigation?.extras?.state?.maintenanceExpiration ?? this.defaultFdeMaintenanceDate);

        combineLatest([
          this.headerService.orgSelectedChanged.pipe(startWith(null), pairwise()),
          this.activatedRoute.params,
          this.sort$,
          this.user$,
        ]).pipe(
          takeUntilDestroyed(this.destroyRef)
        ).subscribe(([[prevOrgId, orgId], params, sort, user]) => {
          const userIsMsp = isMSP(user);
          const canShowMsp = userIsMsp && orgId !== 'All';
          const canShowUser = isCustomer(user);
          if (prevOrgId === null && (canShowMsp || canShowUser)) {
              this.clearLicenseInfo();
              this.query$.next(toFdeQuery(orgId, 0, this.pageSize, params.subscription_id, this.assigned, sort));
          } else {
            void this.router.navigateByUrl('products');
          }

          if (userIsMsp) {
            this.columnsToDisplay = this.mspColumnsToDisplay;
            this.columns.unshift({title: 'Selection', value: 'select', selected: true, hidden: true,});
          }
        });

        this.query$.pipe(
          switchMap(query => this.d3eService.getFdeLicenses(query).pipe(
              withLoading(isLoading => this.isLoading$.next(isLoading))
          )),
          withLatestFrom(this.licenses$),
          takeUntilDestroyed(this.destroyRef)
        ).subscribe(([resp, fdeLicenses]: [IFdeLicenseResponse, IFdeLicense[]]) => {
          this.licenses$.next([...fdeLicenses, ...resp.fdeLicenses]);
          this.subscriptionCount$.next(resp.subscriptionCount);
          this.totalSerials$.next(resp.totalSerials);
          this.totalFiltered$.next(resp.totalFiltered);
          this.totalLicensed$.next(resp.totalLicensed);
          const licensesLoaded = fdeLicenses.length + resp.fdeLicenses.length;
          const validLicensesLoaded = licensesLoaded > 0 ? licensesLoaded : this.pageSize;
          const skeletonRowCount = validLicensesLoaded > this.pageSize ? this.pageSize : validLicensesLoaded;
          this.loadingSkeletonRows = Array(skeletonRowCount).fill({});
        });

    }

    activateDefaultSort(): void {
        const event: {active: string; direction: SortDirection} = {active: 'created_dttm', direction: 'desc'};
        // This does not exist when there are no licenses yet
        if (this.matSort) {
            this.matSort.direction = event.direction;
            this.matSort.sortChange.emit(event);
            this.matSort.active = event.active;
        } else {
            this.onSortChange(event);
        }
    }

    onSortChange(event: Sort): void {
        if (event.active === 'select') {
            return;
        }
        this.licenseSelection.clear();
        this.clearLicenseInfo();
        // This does not exist when there are no licenses yet
        if (this.tableContainer?.nativeElement?.scrollTop) {
            this.tableContainer.nativeElement.scrollTop = 0;
        }
        this.forceCallback = true;
        this.sortAttr = event.active;
        this.sortDir = event.direction;
        this.sort$.next(event);
    }

    showFdeRequestCodeDialog(): void {
        forkJoin([
            this.headerService.orgSelectedChanged.pipe(take(1)),
            this.activatedRoute.params.pipe(take(1)),
        ]).pipe(
            switchMap(([selectedOrgId, params]: [string, Params]) => {
                const selectedOrg = SelectedOrgMethod.getSelectedOrg(selectedOrgId);
                return this.dialog.open(EnterFdeRequestDialogComponent, {
                    width: '550px'
                }).afterClosed().pipe(
                    switchMap(requestCode => {
                        if (requestCode) {
                            return this.d3eService.createFdeLicenseFromRequestCode(selectedOrg, params.subscription_id, requestCode);
                        }
                        return EMPTY;
                    })
                );
            }),
            takeUntilDestroyed(this.destroyRef)
        ).subscribe({
            next: () => {
                this.notification.showSuccess(this.translate.instant('assign.generate-fde-license-dialog.success'));
                this.activateDefaultSort();
            },
            error: (error) => {
                if (error?.status === HttpStatusCode.Conflict) {
                    this.notification.showError(
                        this.translate.instant('assign.fde.generate-license-failed.duplicate.content'),
                        this.translate.instant('assign.fde.generate-license-failed.duplicate.title')
                    );
                } else if (error?.status === HttpStatusCode.PaymentRequired) {
                    if (this.licenses$.getValue().length >= this.subscriptionCount$.getValue()) {
                        this.notification.showError(
                            this.translate.instant('assign.fde.generate-license-failed.none-available.content'),
                            this.translate.instant('assign.fde.generate-license-failed.none-available.title')
                        );
                    } else {
                        this.notification.showError(
                            this.translate.instant('assign.fde.generate-license-failed.expired.content'),
                            this.translate.instant('assign.fde.generate-license-failed.expired.title')
                        );
                    }
                } else {
                    this.notification.showError(
                        this.translate.instant('assign.fde.generate-license-failed.content'),
                        this.translate.instant('assign.fde.generate-license-failed.title')
                    );
                }
            }
        });

    }

    async onCopyResponseCode(fdeLicense: IFdeLicense): Promise<void> {
        const copyText = fdeLicense.license_key
        const params = await firstValueFrom(this.activatedRoute.params);
        this.clipboard.copyMessage(copyText, this.translate.instant('assign.fde.copy-message')).then((success) => {
          if (success) {
            const type = EVENT_TYPES.FDE_LICENSE_COPIED;
            const description = "A Full Drive Encryption License was copied";
            const detail = `subscription: ${params.subscription_id}, fde_serial_uuid: ${fdeLicense.fde_serial_uuid}, license: ${copyText}`;
            this.eventsService.info({type, description, detail}).subscribe();
          }
        });
    }

    showDetailsDialog(workstationId): void {
        forkJoin([
            this.headerService.orgSelectedChanged.pipe(take(1)) as Observable<string>,
            this.activatedRoute.params.pipe(take(1)) as Observable<Params>
        ]).pipe(
            switchMap(([selectedOrgId, params]: [string, Params]) => {
                const selectedOrg = SelectedOrgMethod.getSelectedOrg(selectedOrgId);
                return this.d3eService.getFdeLicenseDetails(selectedOrg, params.subscription_id, workstationId)
            }),
            takeUntilDestroyed(this.destroyRef)
        ).subscribe({
            next: (result) => {
                this.dialog.open(FdeLicenseDetailsComponent, {
                  width: '600px',
                  data: result,
                });
            },
            error: () => {
                this.notification.showError(this.translate.instant('assign.details.failure'));
            }
        });
    }

    async create_download_file(fdeLicense: IFdeLicense): Promise<void> {
        try {
            const params = await firstValueFrom(this.activatedRoute.params);
            const type = EVENT_TYPES.FDE_FILE_DOWNLOAD;
            const description = "A Full Drive Encryption License was downloaded";
            const detail = `subscription: ${params?.subscription_id ?? 'N/A'}, fde_serial_uuid: ${fdeLicense.fde_serial_uuid}, fde_license_id: ${fdeLicense.id}`;
            this.eventsService.info({type, description, detail}).subscribe();
        } catch (error) {
            this.notification.showError(this.translate.instant('assign.details.download-license-failure'));
        }

        const element = document.createElement('a');
        element.setAttribute('href', 'data:text/plain;charset=utf-8,' + encodeURIComponent(fdeLicense.license_key));
        const utcNow = dayjs.utc();
        const compactFileName = utcNow.format('YYYYMMDD[T]HHmmss');
        let filename = `Cigent_FDE_license_${compactFileName}_${fdeLicense.id}.lic`;
        element.setAttribute('download', filename);
        element.style.display = 'none';
        document.body.appendChild(element);
        element.click();
        document.body.removeChild(element);
    }

    showAddWorkstationDialog(): void {
        forkJoin([
            this.headerService.orgSelectedChanged.pipe(take(1)),
            this.activatedRoute.params.pipe(take(1)),
            this.user$.pipe(take(1)),
            this.totalLicensed$.pipe(take(1)),
            this.totalSerials$.pipe(take(1))
        ]).pipe(
            switchMap(([selectedOrgId, params, user, totalLicensed, totalSerials]: [string, Params, IManageUser, number, number]) => {
                const selectedOrg = SelectedOrgMethod.getSelectedOrg(selectedOrgId);
                const data: IAddFdeSerialData = {
                    selectedOrgId: selectedOrg,
                    subscriptionId: params.subscription_id,
                    user: user,
                    maintenanceExpiration: this.maintenanceExpiration,
                    totalLicensed: totalLicensed,
                    totalSerials: totalSerials
                };
                const dialogRef = this.dialog.open(AddWorkstationDialogComponent, {
                    height: 'auto',
                    width: '500px',
                    data: data
                });
                return dialogRef.componentInstance.workstationIdSubmitted.pipe(
                    tap((licensingOption: SerialLicensingOption) => {
                        this.licenseSelection.clear();
                        let event: {active: string; direction: SortDirection} = {active: 'created_dttm', direction: 'desc'};
                        if (licensingOption !== SerialLicensingOption.NO_LICENSE) {
                            event = {active: 'fde_license_id', direction: 'desc'};
                        }
                        this.onSortChange(event);
                    }),
                    switchMap(() => dialogRef.afterClosed())
                );
            }),
            takeUntilDestroyed(this.destroyRef)
        ).subscribe();
    }

    private canLoadNextPage(): boolean {
      const isNotLoading = !this.isLoading$.getValue();
      const licensesLoaded = this.licenses$.getValue().length;
      const hasMoreToLoad = licensesLoaded < this.totalFiltered$.getValue();
      return isNotLoading && hasMoreToLoad;
    }

    onScrollHandler(): void {
        forkJoin([
            this.headerService.orgSelectedChanged.pipe(take(1)),
            this.activatedRoute.params.pipe(take(1)),
            this.sort$.pipe(take(1)),
        ]).pipe(
            tap(([selectedOrgId, params, sort]: [string, Params, any]) => {
                if (this.canLoadNextPage()) {
                  this.forceCallback = false;
                  this.pageStart += this.pageSize;
                  const selectedOrg = SelectedOrgMethod.getSelectedOrg(selectedOrgId);
                  this.query$.next(toFdeQuery(selectedOrg, this.pageStart, this.pageSize, params.subscription_id, this.assigned, sort));
                }
            }),
            takeUntilDestroyed(this.destroyRef)
        ).subscribe();
    }

    clearLicenseInfo(): void {
        this.pageStart = 0;
        this.licenses$.next([]);
        this.subscriptionCount$.next(0);
        this.totalSerials$.next(0);
        this.totalFiltered$.next(0);
        this.totalLicensed$.next(0);
    }

    getColumnWidth(columnValue: string): string | undefined {
        return this.columns.find(col => col.value === columnValue)?.minWidth;
    }

    isAllSelected(): boolean {
        const numSelected = this.licenseSelection.selected.length;
        const licenses: IFdeLicense[] = this.licenses$.getValue();
        return licenses.length > 0 && licenses.length === numSelected;
    }

    toggleAllRows(): void {
      const licenses: IFdeLicense[] = this.licenses$.getValue();

      if (this.isAllSelected()) {
        this.licenseSelection.deselect(...licenses);
      } else {
        this.licenseSelection.select(...licenses);
      }
    }

    showGenerateDialog() {
        if (this.licenseSelection.selected.length === 0) {
            return;
        }

        forkJoin([
            this.headerService.orgSelectedChanged.pipe(take(1)),
            this.activatedRoute.params.pipe(take(1)),
            this.totalLicensed$.pipe(take(1)),
            this.subscriptionCount$.pipe(take(1)),
        ]).pipe(
            switchMap(([selectedOrgId, params, totalLicensed, subscriptionCount]: [string, Params, number, number]) => {
                const totalSelectedWithLicenses = this.licenseSelection.selected.filter(l => !!l.fde_license_id).length;
                const totalLicensesRequired = totalLicensed - totalSelectedWithLicenses + this.licenseSelection.selected.length;
                if (totalLicensesRequired > subscriptionCount) {
                    this.notification.showError(this.translate.instant('assign.generate.not-enough-licenses',
                        {
                            'requested': totalLicensesRequired,
                            'maximum': subscriptionCount
                        })
                    );
                    return EMPTY;
                }

                const selectedOrg = SelectedOrgMethod.getSelectedOrg(selectedOrgId);
                const data = {
                    maintenanceExpiration: this.maintenanceExpiration,
                    selectedCount: this.licenseSelection.selected.length.toString()
                };
                return this.dialog.open(GenerateFdeLicenseDialogComponent, {
                    height: 'auto',
                    data: data
                }).afterClosed().pipe(
                    switchMap(maintenanceExpiration => {
                        if (maintenanceExpiration) {
                            const serials: string[] = this.licenseSelection.selected.map(s => s.fde_serial_uuid);
                            return this.d3eService.createFdeLicenseFromWorkstationIds(selectedOrg, params.subscription_id, maintenanceExpiration, serials);
                        }
                        return EMPTY;
                    })
                );
            }),
            takeUntilDestroyed(this.destroyRef)  // TODO: May want to remove this, test generate a lot of serials and navigate to another page, don't want to kill the transaction
        ).subscribe({
            next: () => {
                this.notification.showSuccess(this.translate.instant('assign.generate-fde-license-dialog.success'));
                const event: {active: string; direction: SortDirection} = {active: 'fde_license_id', direction: 'desc'};
                this.onSortChange(event);
            },
            error: (error) => {
                if (error?.status === HttpStatusCode.Conflict) {
                    this.notification.showError(
                        this.translate.instant('assign.fde.generate-license-failed.duplicate.content'),
                        this.translate.instant('assign.fde.generate-license-failed.duplicate.title')
                    );
                } else if (error?.status === HttpStatusCode.PaymentRequired) {
                    if (this.totalSerials$.getValue() >= this.subscriptionCount$.getValue()) {
                        this.notification.showError(
                            this.translate.instant('assign.fde.generate-license-failed.none-available.content'),
                            this.translate.instant('assign.fde.generate-license-failed.none-available.title')
                        );
                    } else {
                        this.notification.showError(
                            this.translate.instant('assign.fde.generate-license-failed.expired.content'),
                            this.translate.instant('assign.fde.generate-license-failed.expired.title')
                        );
                    }
                } else {
                    this.notification.showError(
                        this.translate.instant('assign.fde.generate-license-failed.content'),
                        this.translate.instant('assign.fde.generate-license-failed.title')
                    );
                }
            }
        });
    }

    onAssignFilterChanged(event: MatButtonToggleChange) {
        this.assigned = event.value;
        forkJoin([
            this.headerService.orgSelectedChanged.pipe(take(1)),
            this.activatedRoute.params.pipe(take(1)),
            this.sort$.pipe(take(1)),
        ]).pipe(
            switchMap(([selectedOrgId, params, sort]: [string, Params, any]) => {
                this.forceCallback = true;
                this.pageStart += this.pageSize;
                const selectedOrg = SelectedOrgMethod.getSelectedOrg(selectedOrgId);
                this.clearLicenseInfo();
                this.licenseSelection.clear();
                this.query$.next(toFdeQuery(selectedOrg, this.pageStart, this.pageSize, params.subscription_id, event.value, sort));
                return of(null);
            }),
            takeUntilDestroyed(this.destroyRef)
        ).subscribe();
    }

    get deleteSerialDisabled(): boolean {
      return this.licenseSelection.selected.length === 0 ||
          this.licenseSelection.selected.some(r => !!r.fde_license_id);
    }

    onCheckboxClick(mouseEvent: MouseEvent, license: IFdeLicense, index: number): void {
        mouseEvent.stopPropagation();
        const licenses = this.licenses$.getValue() ?? [];
        if (mouseEvent.shiftKey && this.lastClickedIndex !== null) {
            const start = Math.min(this.lastClickedIndex, index);
            const end = Math.max(this.lastClickedIndex, index);
            const range = licenses.slice(start, end + 1);
            const shouldSelect = !this.licenseSelection.isSelected(license);

            if (shouldSelect) {
                this.licenseSelection.select(...range);
            } else {
                this.licenseSelection.deselect(...range);
            }
        } else {
            this.licenseSelection.toggle(license);
        }

        this.lastClickedIndex = index;
    }

    showDeleteWorkstationDialog() {
        // Check drives are not licensed
        if (this.licenseSelection.selected.some(l => l.fde_license_id)) {
            this.notification.showError(this.translate.instant('assign.fde.delete-licensed.error'));
            return;
        }

        if (this.licenseSelection.selected.length > this.MAX_DELETE_COUNT) {
            this.notification.showError(this.translate.instant('assign.fde.delete-licensed.max-delete-error', {max: this.MAX_DELETE_COUNT}));
            return;
        }

        forkJoin([
            this.headerService.orgSelectedChanged.pipe(take(1)),
            this.activatedRoute.params.pipe(take(1)),
        ]).pipe(
            switchMap(([selectedOrgId, params]: [string, Params]) => {
                // TODO: Check anything?
                const selectedOrg = SelectedOrgMethod.getSelectedOrg(selectedOrgId);
                return this.dialog.open(AlertDialogComponent, {
                   width: '500px',
                    data: {
                        title: this.translate.instant('assign-fde.delete-dialog-title'),
                        content: `Are you sure you want to delete the ${this.licenseSelection.selected.length} selected workstion IDs`,
                        rejectBtnName: this.translate.instant['NO'],
                        acceptBtnName: this.translate.instant['YES'],
                        showRejectBtn: true,
                    }
                }).afterClosed().pipe(
                    switchMap(result => {
                        if (result) {
                            const workstationIds: string[] = this.licenseSelection.selected.map(l => l.fde_serial_uuid);
                            return this.d3eService.deleteFdeLicenses(selectedOrg, params.subscription_id, workstationIds)
                        }
                        return EMPTY;
                    })
                );
            }),
            takeUntilDestroyed(this.destroyRef)  // TODO: Do we want this, user navigates away while request is in process?
        ).subscribe({
            next: (response) => {
                const deletedCount = response.deleted;
                this.notification.showSuccess(this.translate.instant('assign-fde.delete-success', {deletedCount: deletedCount}));
                const event: {active: string; direction: SortDirection} = {active: 'created_dttm', direction: 'desc'};
                this.onSortChange(event);
            },
            error: (error) => {
                this.notification.showError(this.translate.instant('assign.delete.error'));
            }
        });
    }
}
